import { NgClass } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import {
  ArraySortPipe,
  AutocompleteComponent,
  ControlErrorDirective,
  EnviromentZone,
  GENERAL_CONSTANTS,
  HowdenAlertService,
  HowdenAuthService,
  HowdenTableComponent,
  IButtonActionData,
  IHowdenEnvironment,
  IPageOf,
  ITableEventData,
  PageTitleComponent,
  UIBlockerService
} from '@howdeniberia/core-front';
import { forkJoin, tap } from 'rxjs';
import { APP_ROLES } from '../../core/constants/app-roles.constants';
import { ExecutiveStatus } from '../../core/enums/executive-status.enum';
import { UserRole } from '../../core/enums/user-role.enum';
import { IClientDto } from '../../core/models/client.dto';
import { ClientsContract } from '../../core/services/api/ida-clients/clients.contract';
import { ExecutivesContract } from '../../core/services/api/ida-company/executives.contract';
import { OfficesContract } from '../../core/services/api/ida-company/offices.contract';
import { UsersContract } from '../../core/services/api/ida-company/users.contract';
import { ProfessionsContract } from '../../core/services/api/ida-masters/professions.contract';
import { EventEmitterContract } from '../../core/services/ui/event-emitter.contract';
import { CLIENTS_SEARCH_CONSTANTS } from './../../core/constants/clients-search.constants';
import { IExecutiveDto } from './../../core/models/executive.dto';
import { ClientExtraInfoComponent } from './../client-extra-info/client-extra-info.component';
import { ClientSearchMapper } from './mappers/client-search.mapper';
import { ClientSearchViewModel } from './models/client-search.viewmodel';

@Component({
  selector: 'howden-clients-search-table',
  templateUrl: './clients-search-table.component.html',
  imports: [
    AutocompleteComponent,
    FormsModule,
    HowdenTableComponent,
    MatButton,
    MatDialogModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatIcon,
    MatOption,
    MatSelect,
    MatError,
    ControlErrorDirective,
    NgClass,
    ReactiveFormsModule,
    PageTitleComponent,
    ArraySortPipe
  ]
})
export class ClientsSearchTableComponent implements OnInit {
  private clientsSrv = inject(ClientsContract);
  private executivesSrv = inject(ExecutivesContract);
  private officesSrv = inject(OfficesContract);
  private usersSrv = inject(UsersContract);
  private authSrv = inject(HowdenAuthService);
  private dialog = inject(MatDialog);
  private alertSrv = inject(HowdenAlertService);
  private professionsSrv = inject(ProfessionsContract);
  private route = inject(ActivatedRoute);
  private uiBlockerSrv = inject(UIBlockerService);
  private eventEmitterSrv = inject(EventEmitterContract);
  private howdenEnviroment = inject<IHowdenEnvironment>(GENERAL_CONSTANTS.EnvironmentInjectionToken);

  model = new ClientSearchViewModel();

  firstSearchDone = false;

  @Input() isDialog: boolean = false;

  private _currentExecutive?: IExecutiveDto;
  private _callback?: string;

  ngOnInit(): void {
    if (!this.authSrv.isUserAuthorized([...APP_ROLES.ALL])) {
      return;
    }

    const company = this.authSrv.authData?.accessCompanies?.find(
      (p) => p.companyDataSource === this.howdenEnviroment.fixedTenant
    );

    this.uiBlockerSrv.block();

    forkJoin([
      this.officesSrv.get(),
      this.professionsSrv.get(),
      this.executivesSrv.get(),
      this.usersSrv.get(company?.dataSourceUserID ?? '')
    ]).pipe(
      tap(([offices, professions, executives, connectedUser]) => {
        this.model.offices = offices //Nos quedamos con las oficinas con nombre y ordenamos por nombre
          .filter((p) => p.name)
          .sort((n1, n2) => {
            if (n1.name > n2.name) {
              return 1;
            }
            if (n1.name < n2.name) {
              return -1;
            }
            return 0;
          });

        this.model.professions = professions;

        this.model.executives = executives // Nos quedamos con los ejecutivos activos y ordenamos por nombre
          .filter((p) => p.status === ExecutiveStatus.ACTIVE)
          .sort((n1, n2) => {
            if (n1.name > n2.name) {
              return 1;
            }

            if (n1.name < n2.name) {
              return -1;
            }

            return 0;
          });

        if (this.authSrv.userHasRol(UserRole.ClientsOwned)) {
          this._currentExecutive = executives.find((e) => e.executiveId === connectedUser.executiveId);
        }

        if (!this.isDialog) {
          this.route.queryParams.subscribe((p) => {
            if (p['callback']) {
              this._callback = p['callback'];
            }
          });
        }

        this.uiBlockerSrv.unblock();
      })
    ).subscribe();
  }

  onServerSideConfigChanged(event: ITableEventData): void {
    this.model.updateServerSideConfig(event);
    this.refresh();
  }

  search() {
    this.firstSearchDone = true;
    this.model.searchRequest.pageNumber = 0;

    this.refresh();
  }

  clean() {
    this.firstSearchDone = false;
    this.model.cleanSearch();
  }

  viewClient(selectedClient: IClientDto) {
    if (this._currentExecutive && selectedClient.salesExecutiveId !== this._currentExecutive.executiveId) {
      this.alertSrv.warning(
        $localize`:@@clientsSearch.table.warning: Aviso`,
        $localize`:@@clientsSearch.table.accessErrorMessage: Solo puedes acceder a los clientes en los que estés asignado como ejecutivo`
      );
      return;
    }

    if (this.isDialog) {
      this.eventEmitterSrv.selectClientSearchTable(selectedClient);
    } else {
      let baseUrl = '';
      if (this._callback && this._callback !== '') {
        baseUrl = this._callback;
      } else {
        baseUrl = this.getUrlClientProfile();
      }

      if (baseUrl !== '') {
        window.open(
          `${baseUrl}?CLI_Id=${selectedClient.id}`,
          '_blank' // <- This is what makes it open in a new window.
        );
      }

      return;
    }
  }

  detailInfo(eventData: IButtonActionData) {
    this.dialog.open(ClientExtraInfoComponent, {
      data: eventData.row,
      width: '500px'
    });

    return false;
  }

  private refresh() {
    this.uiBlockerSrv.block();

    this.model.updateServerSideFilters();

    this.clientsSrv.searchClients(this.model.searchRequest).subscribe({
      next: (searchResult: IPageOf<IClientDto>) => {
        this.model.length = searchResult.totalCount;
        this.model.data = searchResult.data?.map((source) =>
          ClientSearchMapper.mapForSearch(source));
      },
      complete: () => {
        this.uiBlockerSrv.unblock();
      }
    });
  }

  private getUrlClientProfile(): string {
    let url = '';

    switch (this.howdenEnviroment.zone) {
      case EnviromentZone.LOCAL:
        url = CLIENTS_SEARCH_CONSTANTS.ClientsProfileRoutes.local ?? '';
        break;
      case EnviromentZone.DEVELOPMENT:
        url = CLIENTS_SEARCH_CONSTANTS.ClientsProfileRoutes.development ?? '';
        break;
      case EnviromentZone.STAGING:
        url = CLIENTS_SEARCH_CONSTANTS.ClientsProfileRoutes.staging ?? '';
        break;
      case EnviromentZone.PRODUCTION:
        url = CLIENTS_SEARCH_CONSTANTS.ClientsProfileRoutes.production ?? '';
        break;
      default:
        url = CLIENTS_SEARCH_CONSTANTS.ClientsProfileRoutes.local ?? ''; //Nunca puede ser null
        break;
    }

    return url;
  }
}
